import Article from '@/models/Article'
import map from 'lodash/map'
import Api from '@/services/api'
import { find } from 'lodash'

export default {
  namespaced: true,
  state: {
    list: [],
    listMeta: {},
    articleCreateAlert: '',
    articleCreateStatus: '',
    articleDetails: {}
  },

  mutations: {
    setArticleList (state, articles) {
      state.list = map(articles, article => {
        if (article instanceof Article) {
          return article
        } else {
          return new Article(article)
        }
      })
    },

    setArticleListMeta (state, meta) {
      state.listMeta = meta
    },

    setArticleCreateAlert (state, payload) {
      state.articleCreateAlert = payload.msg
      state.articleCreateStatus = payload.status
    },

    setArticleDetails (state, payload) {
      state.articleDetails = payload
      if (payload.location) {
        state.articleDetails.us_states_id = payload.location.us_states_id
        state.articleDetails.city_id = payload.location.city_id
      }
    },

    removeArticleListItem (state, index) {
      state.list.splice(index, 1)
    },

    setNewFeatureArticle (state, article) {
      const index = state.list.findIndex(o => o.id === article.id)
      state.list[index] = new Article(article)
    },

    removeFeatureArticle (state, article) {
      const index = state.list.findIndex(o => o.id === article.id)
      if (state.list[index]) state.list[index].featured_article = null
    },

    removeMoreOrderIndex (state, article) {
      const index = state.list.findIndex(o => o.id === article.id)
      if (state.list[index]) state.list[index].more_order_index = null
    }
  },

  actions: {
    async geArticles ({ commit }, params) {
      const query = Article.page(params.page || 1)
        .orderBy(params.sort)
        .limit(20)

      if (params.search) {
        query.where('search', params.search)
      }

      if (params.article_filter !== 'all') {
        query.where(params.article_filter, true)
      }

      if (params.sport_filter !== 'all') {
        query.where('withTagType', params.sport_filter)
      }

      if (params.author_filter) {
        query.where('authorContainingId', params.author_filter)
      }

      if (params.article_category_filter) {
        query.where('articleCategory', params.article_category_filter)
      }

      const res = await query.get()

      commit('setArticleList', res.data)
      commit('setArticleListMeta', res.meta)
    },

    async createArticle ({ commit }, params) {
      const config = {
        headers: { 'content-type': 'multipart/form-data' }
      }

      await Api.post(`admin/articles`, params, config)
        .then(response => {
          let payload = { msg: 'Article Created.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async getArticleDetails ({ commit }, params) {
      let res = await Article.find(params.id)
      commit('setArticleDetails', res.data)
    },

    async updateArticle ({ commit }, params) {
      await Api.put(`admin/articles/${params.id}`, params)
        .then(response => {
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async updateArticleThumbnail ({ commit }, params) {
      let id = params.get('id')

      await Api.post(`admin/articles/${id}/thumbnail`, params)
        .then(response => {
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async deleteArticle ({ commit }, params) {
      await Api.delete(`admin/articles/${params.id}`)
        .then(response => {
          let payload = { msg: 'Article Deleted.', status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })

      commit('removeArticleListItem', params.index)
    },

    async updateIsTrending ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/trending`, params)
        .then(response => {
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async updateMainFeature ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/main-feature`)
        .then(response => {
          let articleData = response.data.data
          let payload = { msg: 'Article Updated.', status: 'success' }

          if (articleData.old_featured) {
            commit('removeFeatureArticle', articleData.old_featured)
          }

          commit('setArticleCreateAlert', payload)
          commit('setNewFeatureArticle', articleData.newly_featured)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async updateSubFeature ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/sub-feature`, params)
        .then(response => {
          let articleData = response.data.data
          let payload = { msg: 'Article Updated.', status: 'success' }

          if (articleData.old_featured) {
            commit('removeFeatureArticle', articleData.old_featured)
          }

          commit('setArticleCreateAlert', payload)
          commit('setNewFeatureArticle', articleData.newly_featured)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async updateMoreOrderIndex ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/more-order`, params)
        .then(response => {
          let articleData = response.data.data

          let payload = { msg: 'Article Updated.', status: 'success' }

          if (articleData.old_order_index) {
            commit('removeMoreOrderIndex', articleData.old_order_index)
          }

          commit('setArticleCreateAlert', payload)
          commit('setNewFeatureArticle', articleData.new_order_index)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async publishArticle ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/republish`)
        .then(response => {
          console.log(response.data)
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async unpublishArticle ({ commit }, params) {
      let id = params.id

      await Api.put(`admin/articles/${id}/unpublish`)
        .then(response => {
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async updateSocialThumbnail ({ commit }, params) {
      let id = params.get('id')

      await Api.post(`admin/articles/${id}/social-thumbnail`, params)
        .then(response => {
          let payload = { msg: 'Article Updated.', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    },

    async generateArticleSlug ({ commit }, params) {
      let { data } = await Api.post('admin/articles/slug/generate', params)

      return data
    },

    async cloneArticle ({ commit }, id) {
      await Api.post(`admin/articles/${id}/duplicate`)
        .then(response => {
          let payload = { msg: 'Article Duplicated!', status: 'success' }
          commit('setArticleCreateAlert', payload)
        })
        .catch(error => {
          let payload = { msg: error.response.data.message, status: 'error' }
          commit('setArticleCreateAlert', payload)
        })
    }
  }
}
